import { useEffect, useRef } from "react";
import { centroid, panelWH } from "./layoutMath";

function PanelShape({ p, mod, index, selected, onDown }) {
  const [w, h] = panelWH(p, mod);
  const x0 = -w / 2;
  const y0 = -h / 2;
  const segs = p.landscape
    ? [[-w / 6, y0, -w / 6, -y0], [w / 6, y0, w / 6, -y0], [x0, 0, -x0, 0]]
    : [[x0, -h / 6, -x0, -h / 6], [x0, h / 6, -x0, h / 6], [0, y0, 0, -y0]];
  return (
    <g data-panel="1" transform={`translate(${p.x} ${p.y}) rotate(${p.angle || 0})`} onPointerDown={(e) => onDown(e, p)}
      style={{ cursor: "grab", touchAction: "none" }} data-testid={`roof-layout-panel-${index}`}
      data-selected={selected ? "true" : "false"} data-landscape={p.landscape ? "true" : "false"}>
      <rect x={x0} y={y0} width={w} height={h} fill="#0F2442" fillOpacity="0.92"
        stroke={selected ? "#00E575" : "#E2E8F0"} strokeWidth={selected ? 2.5 : 1} vectorEffect="non-scaling-stroke" />
      {segs.map(([a, b, c, d], i) => (
        <line key={i} x1={a} y1={b} x2={c} y2={d} stroke="#94A3B8" strokeOpacity="0.7" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      ))}
    </g>
  );
}

export function LayoutStage({ img, panels, mod, selected, mode, zoom, onSelect, setPanels, onAdd, onKey }) {
  const svgRef = useRef(null);
  const boxRef = useRef(null);
  const drag = useRef(null);
  const toPt = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };
  const panelsRef = useRef(panels);
  panelsRef.current = panels;
  useEffect(() => {
    const el = boxRef.current;
    const ps = panelsRef.current;
    const c = ps.length ? centroid(ps) : { x: img.w / 2, y: img.h / 2 };
    el.scrollLeft = (c.x / img.w) * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = (c.y / img.h) * el.scrollHeight - el.clientHeight / 2;
  }, [zoom, img]);
  useEffect(() => {
    const svg = svgRef.current;
    const stop = (e) => { if (e.target.closest?.("[data-panel]")) e.preventDefault(); };
    svg.addEventListener("touchstart", stop, { passive: false });
    return () => svg.removeEventListener("touchstart", stop);
  }, []);

  const down = (e, p) => {
    e.stopPropagation();
    onSelect(p.id);
    const moving = mode === "moveAll" ? panels : [p];
    drag.current = { pt: toPt(e), orig: Object.fromEntries(moving.map((q) => [q.id, { x: q.x, y: q.y }])) };
    svgRef.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const pt = toPt(e);
    const dx = pt.x - d.pt.x;
    const dy = pt.y - d.pt.y;
    if (!dx && !dy) return;
    setPanels((ps) => ps.map((q) => (d.orig[q.id] ? { ...q, x: d.orig[q.id].x + dx, y: d.orig[q.id].y + dy } : q)));
  };
  const up = () => { drag.current = null; };
  const bgClick = (e) => (mode === "add" ? onAdd(toPt(e)) : onSelect(null));

  return (
    <div ref={boxRef} tabIndex={0} onKeyDown={onKey}
      className="relative overflow-auto max-h-[68vh] rounded-xl border border-zinc-800 bg-black outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5E9]/60"
      data-testid="roof-layout-stage">
      <div className="relative" style={{ width: `${zoom * 100}%` }}>
        <img src={img.url} alt="Dachbild" draggable={false} className="block w-full h-auto select-none pointer-events-none" data-testid="roof-layout-image" />
        <svg ref={svgRef} viewBox={`0 0 ${img.w} ${img.h}`} className={`absolute inset-0 w-full h-full ${mode === "add" ? "cursor-crosshair" : ""}`}
          onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="roof-layout-svg">
          <rect width={img.w} height={img.h} fill="transparent" onClick={bgClick} data-testid="roof-layout-bg" />
          {panels.map((p, i) => (
            <PanelShape key={p.id} p={p} mod={mod} index={i} selected={p.id === selected} onDown={down} />
          ))}
        </svg>
      </div>
    </div>
  );
}
